import { useEffect, useState } from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Table, Button, Modal, Form } from 'react-bootstrap';
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Message from '../../Components/Message';
import Loader from '../../Components/Loader';
import { toast } from 'sonner';
import {
  useGetProductsQuery,
  useDeleteProductMutation,
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from '../../slices/productsApiSlice';
import Paginate from '../../Components/Paginate';
import { getCloudinaryImage } from '../../utils/cloudinary';
import AdminTableToolbar from '../../Components/AdminTableToolbar';
import AdminFilterPanel from '../../Components/AdminFilterPanel';
import ConfirmationModal from '../../Components/ConfirmationModal';

import '../../assets/styles/components/ProductListScreen.css';

const ProductListScreen = () => {
  const { pageNumber } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const keyword = searchParams.get('keyword') || '';
  const sort = searchParams.get('sort') || 'latest';

  const adminStock = searchParams.get('adminStock') || '';
  const adminPriceGte = searchParams.get('adminPriceGte') || '';
  const adminPriceLte = searchParams.get('adminPriceLte') || '';

  const adminCategories = searchParams.getAll('adminCategory');

  const adminVisibility = searchParams.getAll('adminVisibility');

  const [searchValue, setSearchValue] = useState(keyword);
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const [priceMin, setPriceMin] = useState(adminPriceGte);
  const [priceMax, setPriceMax] = useState(adminPriceLte);
  const [stockFilter, setStockFilter] = useState(adminStock);

  const [selectedCategories, setSelectedCategories] = useState(adminCategories);

  const [selectedVisibility, setSelectedVisibility] = useState(adminVisibility);

  const { data, isLoading, error, refetch } = useGetProductsQuery({
    admin: true,
    pageNumber,
    adminSearch: keyword,
    adminStock,
    adminPriceGte,
    adminPriceLte,
    adminCategories,
    adminVisibility,
    sort,
  });

  const {
    data: categories,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useGetCategoriesQuery();

  const [createCategory, { isLoading: loadingCreateCategory }] =
    useCreateCategoryMutation();

  const [updateCategory, { isLoading: loadingUpdateCategory }] =
    useUpdateCategoryMutation();

  const [deleteCategory, { isLoading: loadingDeleteCategory }] =
    useDeleteCategoryMutation();

  const [deleteProduct, { isLoading: loadingDelete }] =
    useDeleteProductMutation();

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const productSortOptions = [
    { value: 'latest', label: 'Latest' },
    { value: 'price-asc', label: 'Price Low → High' },
    { value: 'price-desc', label: 'Price High → Low' },
    { value: 'rating', label: 'Average Rating' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedSearch = searchValue.trim();
      const currentKeyword = searchParams.get('keyword') || '';

      if (trimmedSearch === currentKeyword) {
        return;
      }

      const params = new URLSearchParams(searchParams);

      if (trimmedSearch) {
        params.set('keyword', trimmedSearch);
      } else {
        params.delete('keyword');
      }

      navigate(
        {
          pathname: '/admin/productlist',
          search: params.toString(),
        },
        { replace: true },
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [searchValue, searchParams, navigate]);

  const openCategoryModal = () => {
    setShowCategoryModal(true);
    setCategoryName('');
    setEditingCategory(null);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setCategoryName('');
    setEditingCategory(null);
  };

  const submitCategoryHandler = async (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      toast.warning('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        await updateCategory({
          categoryId: editingCategory._id,
          name: categoryName.trim(),
        }).unwrap();

        toast.success('Category updated');
      } else {
        await createCategory({
          name: categoryName.trim(),
        }).unwrap();

        toast.success('Category added');
      }

      setCategoryName('');
      setEditingCategory(null);
    } catch (error) {
      toast.error(
        error?.data?.error ||
          error?.data?.message ||
          error?.error ||
          'Something went wrong',
      );
    }
  };

  const editCategoryHandler = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
  };

  const deleteCategoryHandler = async (category) => {
    try {
      await deleteCategory(category._id).unwrap();

      toast.success('Category deleted');
    } catch (error) {
      toast.error(
        error?.data?.error ||
          error?.data?.message ||
          error?.error ||
          'Unable to delete category',
      );
    }
  };

  const toggleCategory = (categoryName) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((category) => category !== categoryName)
        : [...prev, categoryName],
    );
  };

  const toggleVisibility = (visibility) => {
    setSelectedVisibility((prev) =>
      prev.includes(visibility)
        ? prev.filter((item) => item !== visibility)
        : [...prev, visibility],
    );
  };

  const openFilterPanel = () => {
    setPriceMin(searchParams.get('adminPriceGte') || '');
    setPriceMax(searchParams.get('adminPriceLte') || '');
    setStockFilter(searchParams.get('adminStock') || '');

    setSelectedCategories(searchParams.getAll('adminCategory'));
    setSelectedVisibility(searchParams.getAll('adminVisibility'));

    setFilterOpen(true);
  };

  const deleteHandler = async (slug) => {
    try {
      await deleteProduct(slug).unwrap();
      toast.success('Product Deleted');
      refetch();
    } catch (error) {
      toast.error(
        error?.data?.error ||
          error?.data?.message ||
          error?.error ||
          'Unable to delete product',
      );
    }
  };

  const createProductHandler = () => {
    navigate('/admin/product/create');
  };

  const formatPrice = (price) => new Intl.NumberFormat('en-IN').format(price);

  const getTotalStock = (product) =>
    product.variants.reduce(
      (total, variant) => total + Number(variant.countInStock || 0),
      0,
    );

  const handleApplyFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Price
    if (priceMin) {
      params.set('adminPriceGte', priceMin);
    } else {
      params.delete('adminPriceGte');
    }

    if (priceMax) {
      params.set('adminPriceLte', priceMax);
    } else {
      params.delete('adminPriceLte');
    }

    // Stock
    if (stockFilter) {
      params.set('adminStock', stockFilter);
    } else {
      params.delete('adminStock');
    }

    // Categories
    params.delete('adminCategory');

    selectedCategories.forEach((category) => {
      params.append('adminCategory', category);
    });

    // Visibility
    params.delete('adminVisibility');

    selectedVisibility.forEach((visibility) => {
      params.append('adminVisibility', visibility);
    });

    // Start from page 1 when filters change
    params.delete('pageNumber');

    setFilterOpen(false);

    navigate(
      {
        pathname: '/admin/productlist',
        search: params.toString(),
      },
      { replace: true },
    );
  };

  const handleResetFilters = () => {
    setPriceMin('');
    setPriceMax('');
    setStockFilter('');
    setSelectedCategories([]);
    setSelectedVisibility([]);

    const params = new URLSearchParams(searchParams);

    params.delete('adminPriceGte');
    params.delete('adminPriceLte');
    params.delete('adminStock');
    params.delete('adminCategory');
    params.delete('adminVisibility');
    params.delete('pageNumber');

    setFilterOpen(false);

    navigate(
      {
        pathname: '/admin/productlist',
        search: params.toString(),
      },
      { replace: true },
    );
  };

  const handleSortChange = (value) => {
    const params = new URLSearchParams(searchParams);

    if (value === 'latest') {
      params.delete('sort');
    } else {
      params.set('sort', value);
    }

    setSortOpen(false);

    navigate(
      {
        pathname: '/admin/productlist',
        search: params.toString(),
      },
      { replace: true },
    );
  };

  const hasOutOfStockVariant = (product) =>
    product.variants.some((variant) => Number(variant.countInStock || 0) <= 0);

  const filterCount =
    (adminPriceGte || adminPriceLte ? 1 : 0) +
    (adminStock ? 1 : 0) +
    adminCategories.length +
    adminVisibility.length;

  return (
    <div className='admin-products-page'>
      <div className='admin-products-header'>
        <div>
          <p className='admin-products-subtitle'>ADMIN PANEL</p>
          <h1 className='admin-products-title'>Products</h1>
          <p className='admin-products-description'>
            Manage your product catalog
          </p>
        </div>

        <div className='admin-products-header-actions'>
          <Button
            className='admin-products-category-btn'
            onClick={openCategoryModal}
          >
            Manage Categories
          </Button>

          <Button
            className='admin-products-create-btn'
            onClick={createProductHandler}
          >
            + New Product
          </Button>
        </div>
      </div>

      <AdminTableToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder='Search products...'
        onFilterClick={openFilterPanel}
        filterCount={filterCount}
        sortValue={sort}
        sortOptions={productSortOptions}
        sortOpen={sortOpen}
        onSortToggle={() => setSortOpen((prev) => !prev)}
        onSortChange={handleSortChange}
      />

      <AdminFilterPanel
        show={filterOpen}
        onClose={() => setFilterOpen(false)}
        onReset={handleResetFilters}
        onApply={handleApplyFilters}
        filterCount={filterCount}
      >
        <div className='admin-filter-section'>
          <h4>Price</h4>

          <div className='admin-filter-price-row'>
            <input
              type='number'
              min='0'
              placeholder='Min'
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
            />

            <span>—</span>

            <input
              type='number'
              min='0'
              placeholder='Max'
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
            />
          </div>
        </div>

        <div className='admin-filter-section'>
          <h4>Stock</h4>

          <div className='admin-filter-radio-group'>
            <label>
              <input
                type='radio'
                name='admin-stock-filter'
                checked={stockFilter === ''}
                onChange={() => setStockFilter('')}
              />
              <span>All</span>
            </label>

            <label>
              <input
                type='radio'
                name='admin-stock-filter'
                checked={stockFilter === 'in-stock'}
                onChange={() => setStockFilter('in-stock')}
              />
              <span>In Stock</span>
            </label>

            <label>
              <input
                type='radio'
                name='admin-stock-filter'
                checked={stockFilter === 'out-stock'}
                onChange={() => setStockFilter('out-stock')}
              />
              <span>Out of Stock</span>
            </label>
          </div>
        </div>

        <div className='admin-filter-section'>
          <h4>Category</h4>

          <div className='admin-filter-checkbox-group'>
            {categories?.map((category) => (
              <label key={category._id}>
                <input
                  type='checkbox'
                  checked={selectedCategories.includes(category.name)}
                  onChange={() => toggleCategory(category.name)}
                />

                <span>{category.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className='admin-filter-section'>
          <h4>Visibility</h4>

          <div className='admin-filter-checkbox-group'>
            <label>
              <input
                type='checkbox'
                checked={selectedVisibility.includes('featured')}
                onChange={() => toggleVisibility('featured')}
              />
              <span>Featured</span>
            </label>

            <label>
              <input
                type='checkbox'
                checked={selectedVisibility.includes('new-arrival')}
                onChange={() => toggleVisibility('new-arrival')}
              />
              <span>New Arrival</span>
            </label>

            <label>
              <input
                type='checkbox'
                checked={selectedVisibility.includes('best-seller')}
                onChange={() => toggleVisibility('best-seller')}
              />
              <span>Best Seller</span>
            </label>
          </div>
        </div>
      </AdminFilterPanel>

      <div className='admin-products-content'>
        {loadingDelete && <Loader />}

        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant='danger'>{error}</Message>
        ) : (
          <>
            {/* Desktop / Tablet */}
            <div className='admin-products-table-wrapper'>
              <Table responsive className='admin-products-table align-middle'>
                <thead>
                  <tr>
                    <th>IMAGE</th>
                    <th>NAME</th>
                    <th>STYLES</th>
                    <th>PRICE</th>
                    <th>STOCK</th>
                    <th>CATEGORY</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {data.products.map((product) => (
                    <tr key={product._id}>
                      <td>
                        <img
                          src={getCloudinaryImage(
                            product.variants[0]?.images[0],
                            'w_120,h_120,c_fill,f_auto,q_auto',
                          )}
                          alt={product.name}
                          className='admin-products-image'
                        />
                      </td>

                      <td>
                        <Link
                          to={`/products/${product.slug}`}
                          className='admin-products-name'
                        >
                          {product.name}
                        </Link>
                      </td>

                      <td>
                        <div className='admin-products-variants'>
                          {product.variants.map((variant, index) => (
                            <span
                              key={index}
                              className='admin-products-variant'
                            >
                              {variant.color}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className='admin-products-price'>
                        ₹{formatPrice(product.variants[0]?.price)}
                      </td>

                      <td>
                        <div className='admin-products-stock-details'>
                          <div
                            className={`admin-products-stock ${
                              hasOutOfStockVariant(product)
                                ? 'out-of-stock'
                                : 'in-stock'
                            }`}
                          >
                            <span className='admin-products-status-dot' />
                            {getTotalStock(product) > 0
                              ? `${getTotalStock(product)} Total`
                              : 'Out of Stock'}
                          </div>

                          <div className='admin-products-variant-stock'>
                            {product.variants.map((variant) => (
                              <div
                                key={variant._id}
                                className='admin-products-variant-stock-row'
                              >
                                <span>{variant.color}</span>
                                <span>{variant.countInStock}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className='admin-products-category'>
                          {product.category}
                        </span>
                      </td>

                      <td>
                        <div className='admin-products-actions'>
                          <LinkContainer
                            to={`/admin/product/${product.slug}/edit`}
                          >
                            <Button
                              variant='light'
                              className='admin-products-edit-btn'
                              aria-label={`Edit ${product.name}`}
                              title='Edit product'
                            >
                              <FaEdit />
                            </Button>
                          </LinkContainer>

                          <Button
                            variant='light'
                            className='admin-products-delete-btn'
                            onClick={() => {
                              setDeleteTarget({
                                type: 'product',
                                id: product.slug,
                                name: product.name,
                              });
                              setShowDeleteConfirmation(true);
                            }}
                            aria-label={`Delete ${product.name}`}
                            title='Delete product'
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Mobile */}
            <div className='admin-products-mobile-list'>
              {data.products.map((product) => (
                <div className='admin-product-card' key={product._id}>
                  <div className='admin-product-card-main'>
                    <img
                      src={getCloudinaryImage(
                        product.variants[0]?.images[0],
                        'w_160,h_160,c_fill,f_auto,q_auto',
                      )}
                      alt={product.name}
                      className='admin-product-card-image'
                    />

                    <div className='admin-product-card-info'>
                      <Link
                        to={`/products/${product.slug}`}
                        className='admin-product-card-name'
                      >
                        {product.name}
                      </Link>

                      <div className='admin-product-card-variants'>
                        {product.variants.map((variant, index) => (
                          <span key={index} className='admin-products-variant'>
                            {variant.color}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className='admin-product-card-details'>
                    <div>
                      <span className='admin-product-card-label'>Price</span>
                      <strong className='admin-products-price'>
                        ₹{formatPrice(product.variants[0]?.price)}
                      </strong>
                    </div>

                    <div>
                      <span className='admin-product-card-label'>Stock</span>

                      <div className='admin-products-stock-details'>
                        <div
                          className={`admin-products-stock ${
                            hasOutOfStockVariant(product)
                              ? 'out-of-stock'
                              : 'in-stock'
                          }`}
                        >
                          <span className='admin-products-status-dot' />
                          {getTotalStock(product) > 0
                            ? `${getTotalStock(product)} Total`
                            : 'Out of Stock'}
                        </div>

                        <div className='admin-products-variant-stock'>
                          {product.variants.map((variant) => (
                            <div
                              key={variant._id}
                              className='admin-products-variant-stock-row'
                            >
                              <span>{variant.color}</span>
                              <span>{variant.countInStock}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <span className='admin-product-card-label'>Category</span>
                      <span className='admin-products-category'>
                        {product.category}
                      </span>
                    </div>
                  </div>

                  <div className='admin-product-card-actions'>
                    <LinkContainer to={`/admin/product/${product.slug}/edit`}>
                      <Button
                        variant='light'
                        className='admin-product-card-edit-btn'
                      >
                        <FaEdit />
                        <span>Edit</span>
                      </Button>
                    </LinkContainer>

                    <Button
                      variant='light'
                      className='admin-product-card-delete-btn'
                      onClick={() => {
                        setDeleteTarget({
                          type: 'product',
                          id: product.slug,
                          name: product.name,
                        });
                        setShowDeleteConfirmation(true);
                      }}
                    >
                      <FaTrash />
                      <span>Delete</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className='admin-products-pagination'>
              <Paginate pages={data.pages} page={data.page} isAdmin={true} />
            </div>
          </>
        )}
      </div>
      <Modal
        show={showCategoryModal}
        onHide={closeCategoryModal}
        centered
        className='admin-category-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage Categories</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={submitCategoryHandler}>
            <Form.Group className='admin-category-form-group'>
              <Form.Label>
                {editingCategory ? 'Rename category' : 'Add category'}
              </Form.Label>

              <div className='admin-category-input-row'>
                <Form.Control
                  type='text'
                  placeholder='Enter category name'
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  disabled={loadingCreateCategory || loadingUpdateCategory}
                />

                <Button
                  type='submit'
                  className='admin-category-submit-btn'
                  disabled={loadingCreateCategory || loadingUpdateCategory}
                >
                  {editingCategory ? 'Save' : 'Add'}
                </Button>
              </div>
            </Form.Group>
          </Form>

          <div className='admin-category-list'>
            <h6>Categories</h6>

            {categoriesLoading ? (
              <Loader />
            ) : categoriesError ? (
              <Message variant='danger'>
                {categoriesError?.dat?.error ||
                  categoriesError?.data?.message ||
                  categories?.error ||
                  'Unable to load categories'}
              </Message>
            ) : categories?.length === 0 ? (
              <p className='admin-category-empty'>No categories found.</p>
            ) : (
              categories.map((category) => (
                <div className='admin-category-item' key={category._id}>
                  <span>{category.name}</span>

                  <div className='admin-category-actions'>
                    <Button
                      className='admin-category-edit-btn'
                      onClick={() => editCategoryHandler(category)}
                      disabled={
                        loadingCreateCategory ||
                        loadingUpdateCategory ||
                        loadingDeleteCategory
                      }
                      aria-label={`Edit ${category.name}`}
                      title='Edit category'
                    >
                      <FaEdit />
                    </Button>

                    <Button
                      className='admin-category-delete-btn'
                      onClick={() => {
                        setDeleteTarget({
                          type: 'category',
                          id: category._id,
                          name: category.name,
                        });
                        setShowDeleteConfirmation(true);
                      }}
                      disabled={
                        loadingCreateCategory ||
                        loadingUpdateCategory ||
                        loadingDeleteCategory
                      }
                      aria-label={`Delete ${category.name}`}
                      title='Delete category'
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className='admin-category-close-btn'
            onClick={closeCategoryModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showDeleteConfirmation}
        title={`Delete ${deleteTarget?.type === 'category' ? 'Category' : 'Product'}?`}
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmText='Delete'
        cancelText='Cancel'
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setDeleteTarget(null);
        }}
        onConfirm={async () => {
          if (!deleteTarget) return;

          if (deleteTarget.type === 'category') {
            await deleteCategoryHandler(deleteTarget.id);
          } else {
            await deleteHandler(deleteTarget.id);
          }

          setShowDeleteConfirmation(false);
          setDeleteTarget(null);
        }}
        isLoading={
          deleteTarget?.type === 'category'
            ? loadingDeleteCategory
            : loadingDelete
        }
        loadingText='Deleting...'
      />
    </div>
  );
};

export default ProductListScreen;
